import React, { useState, useRef, useEffect, useCallback } from 'react';
import { observer } from 'mobx-react';
import { Link } from 'react-router-dom';
import { STAGE_CONFIG, formatDisplayId, formatAge } from '../../models/LiveMeetingsStore';
import ApprovalPanel   from './rows/ApprovalPanel';
import TriagePanel     from './rows/TriagePanel';
import FailedPanel     from './rows/FailedPanel';
import ProcessingPanel from './rows/ProcessingPanel';
import {
  ExpandedPanel,
  TableCard,
  TableHeader,
  TableHeaderCell,
  EmptyState,
  EmptyStateIcon,
  EmptyStateText,
  RowWrapper,
  RowMain,
  ChevronCell,
  IdCell,
  MeetingCell,
  MeetingTitle,
  MeetingUrl,
  AgeCell,
  IssueCell,
  ActionsCell,
  IconButton,
  StageBadge,
  StatusBadge,
  OverflowMenuWrapper,
  OverflowMenu,
  OverflowMenuItem,
  SkeletonRow,
  Overlay,
  ModalCard,
  ModalHeader,
  ModalTitle,
  ModalBody,
  ModalCloseButton,
  RawDataPre,
} from './LiveMeetingsDashboard.styles';

// ─── Retrying panel ──────────────────────────────────────────────────────────

function RetryingPanel() {
  return (
    <ExpandedPanel>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#0784a7', fontSize: 14 }}>
        <span style={{ fontSize: 18 }}>↻</span>
        <span>Queued for retry — reprocessing will begin shortly.</span>
      </div>
    </ExpandedPanel>
  );
}

// ─── Row ─────────────────────────────────────────────────────────────────────

function MeetingRow({ item, liveStore, adminStore, currentUserName, onAction }) {
  const [expanded, setExpanded]       = useState(false);
  const [menuOpen, setMenuOpen]       = useState(false);
  const [triageMode, setTriageMode]   = useState(null);   // 'assign' | 'review'
  const [showRaw, setShowRaw]         = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [retrying, setRetrying]       = useState(false);
  const menuRef                       = useRef(null);

  // Close overflow menu when clicking outside
  useEffect(() => {
    if (!menuOpen) return;
    const handler = e => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  const toggleExpand = useCallback(() => {
    setExpanded(v => !v);
    setTriageMode(null);
  }, []);

  const handleMenuAction = useCallback(async (action) => {
    setMenuOpen(false);
    if (action === 'approve') {
      await onAction(() => liveStore.approveRequest(item.approvalData.requestId, currentUserName));
    } else if (action === 'deny') {
      await onAction(() => liveStore.denyRequest(item.approvalData.requestId, currentUserName));
    } else if (action === 'assign') {
      setExpanded(true);
      setTriageMode('assign');
    } else if (action === 'review') {
      setExpanded(true);
      setTriageMode('review');
    } else if (action === 'retry') {
      setRetrying(true);
      setExpanded(true);
      await onAction(() => liveStore.retryFailed(item.dlqData?.id));
      setRetrying(false);
    } else if (action === 'escalate') {
      await onAction(() => liveStore.escalate(item.id));
    }
  }, [item, liveStore, currentUserName, onAction]);

  const cfg = STAGE_CONFIG[item.stage] || {};

  // Determine status badge text + variant
  let statusText    = '';
  let statusVariant = 'default';
  if (item.stage === 'approval_pending') { statusText = 'Pending Approval'; statusVariant = 'pending'; }
  else if (item.stage === 'needs_triage') {
    statusText    = item.triageData?.priority === 'high' ? 'High Priority' : 'Needs Review';
    statusVariant = item.triageData?.priority === 'high' ? 'high' : 'pending';
  } else if (item.stage === 'failed')    { statusText = 'Failed'; statusVariant = 'high'; }
  else if (item.stage === 'delivered')   { statusText = 'Complete'; statusVariant = 'done'; }
  else if (item.stage === 'email_sent')  { statusText = 'Complete'; statusVariant = 'done'; }
  else if (item.stage === 'recording')   { statusText = 'Live'; statusVariant = 'active'; }
  else if (item.stage === 'analyzing')   { statusText = 'In Progress'; statusVariant = 'active'; }

  // Which panel to render
  let panel = null;
  if (expanded) {
    if (item.stage === 'approval_pending') {
      panel = <ApprovalPanel item={item} />;
    } else if (item.stage === 'needs_triage') {
      panel = <TriagePanel item={item} liveStore={liveStore} adminStore={adminStore} mode={triageMode} />;
    } else if (item.stage === 'failed') {
      panel = retrying
        ? <RetryingPanel />
        : <FailedPanel item={item} />;
    } else {
      panel = <ProcessingPanel item={item} />;
    }
  }

  // Which overflow menu items to show
  const menuItems = [];
  if (item.stage === 'approval_pending') {
    menuItems.push({ key: 'approve', label: '✓  Approve', action: 'approve' });
    menuItems.push({ key: 'deny',    label: '✗  Deny',    action: 'deny', danger: true });
  } else if (item.stage === 'needs_triage') {
    menuItems.push({ key: 'assign', label: 'Assign…',           action: 'assign' });
    menuItems.push({ key: 'review', label: 'Mark as Reviewed…', action: 'review' });
  } else if (item.stage === 'failed') {
    if (item.dlqData?._dlqCategory === 'retriable') {
      menuItems.push({ key: 'retry', label: '↻  Retry', action: 'retry' });
    }
    menuItems.push({ key: 'escalate', label: 'Escalate to Tier 2', action: 'escalate' });
  }

  const displayId = formatDisplayId(
    item.approvalData?.requestId ||
    item.dlqData?.id ||
    item.triageData?.botId ||
    item.meetingData?.id ||
    item.meetingData?.botId ||
    item.id
  );

  return (
    <RowWrapper>
      <RowMain $expanded={expanded} onClick={toggleExpand}>
        {/* Chevron */}
        <ChevronCell $expanded={expanded}>›</ChevronCell>

        {/* ID */}
        <IdCell title={displayId}>{displayId}</IdCell>

        {/* Meeting */}
        <MeetingCell>
          <MeetingTitle title={item.meetingTitle || '(Untitled)'}>{item.meetingTitle || '(Untitled)'}</MeetingTitle>
          {item.meetingUrl && (
            <MeetingUrl
              href={item.meetingUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
            >
              {item.meetingUrl}
            </MeetingUrl>
          )}
        </MeetingCell>

        {/* Stage */}
        <div>
          <StageBadge $stage={item.stage}>{cfg.icon} {cfg.label}</StageBadge>
        </div>

        {/* Status */}
        <div>
          {statusText && <StatusBadge $variant={statusVariant}>{statusText}</StatusBadge>}
        </div>

        {/* Age */}
        <AgeCell>{formatAge(item.createdAt || item.scheduledStartTime)}</AgeCell>

        {/* Issue */}
        <IssueCell title={item.issueText}>{item.issueText}</IssueCell>

        {/* Actions */}
        <ActionsCell onClick={e => e.stopPropagation()}>
          {/* History popup */}
          <IconButton
            title="Processing history"
            onClick={() => setShowHistory(true)}
          >
            🕐
          </IconButton>

          {/* Raw data */}
          <IconButton
            title="Raw data"
            onClick={() => setShowRaw(true)}
          >
            {'<>'}
          </IconButton>

          {/* Bot Inspector — cross-table DDB view */}
          {(() => {
            const inspectorId = item.meetingData?.botId || item.meetingData?.id || item.triageData?.botId || item.dlqData?.id || item.id;
            if (!inspectorId) return null;
            return (
              <IconButton
                as={Link}
                to={`/murphy/bot-inspector/${encodeURIComponent(inspectorId)}`}
                title="Inspect in DynamoDB tables"
                onClick={e => e.stopPropagation()}
              >
                🔍
              </IconButton>
            );
          })()}

          {/* Overflow ⋮ */}
          {menuItems.length > 0 && (
            <OverflowMenuWrapper ref={menuRef}>
              <IconButton
                title="Actions"
                onClick={() => setMenuOpen(v => !v)}
                style={{ fontSize: 18, fontWeight: 700 }}
              >
                ⋮
              </IconButton>
              {menuOpen && (
                <OverflowMenu>
                  {menuItems.map(mi => (
                    <OverflowMenuItem
                      key={mi.key}
                      $danger={mi.danger}
                      onClick={() => handleMenuAction(mi.action)}
                    >
                      {mi.label}
                    </OverflowMenuItem>
                  ))}
                </OverflowMenu>
              )}
            </OverflowMenuWrapper>
          )}
        </ActionsCell>
      </RowMain>

      {/* Expanded panel */}
      {panel}

      {/* Raw data modal */}
      {showRaw && (
        <RawDataModal item={item} onClose={() => setShowRaw(false)} />
      )}

      {/* History modal */}
      {showHistory && (
        <HistoryModal item={item} onClose={() => setShowHistory(false)} />
      )}
    </RowWrapper>
  );
}

// ─── Raw data modal ───────────────────────────────────────────────────────────

function RawDataModal({ item, onClose }) {
  // Strip internal-only fields for display
  const { urgencyScore, issueText, sourceType, ...display } = item;
  return (
    <Overlay onClick={onClose}>
      <ModalCard onClick={e => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Raw Data — {item.id}</ModalTitle>
          <ModalCloseButton onClick={onClose}>✕</ModalCloseButton>
        </ModalHeader>
        <ModalBody>
          <RawDataPre>{JSON.stringify(display, null, 2)}</RawDataPre>
        </ModalBody>
      </ModalCard>
    </Overlay>
  );
}

// ─── History modal ────────────────────────────────────────────────────────────

function HistoryModal({ item, onClose }) {
  // Build a simple ordered event list from available timestamps
  const events = [];
  const add = (label, ts) => { if (ts) events.push({ label, ts }); };

  add('Approval requested',       item.approvalData?.createdAt);
  add('Approval granted',         item.approvalData?.approvedAt);
  add('Meeting scheduled',        item.scheduledStartTime);
  add('Recording started',        item.meetingData?.recordingStartedAt);
  add('Queued for processing',    item.meetingData?.queuedAt);
  add('Transcription started',    item.meetingData?.transcriptionStartedAt);
  add('Transcription complete',   item.meetingData?.transcriptionCompletedAt);
  add('Participants identified',  item.meetingData?.identifiedAt);
  add('Triage created',           item.triageData?.createdAt);
  add('Assigned',                 item.meetingData?.assignedAt);
  add('Analysis started',         item.meetingData?.analysisStartedAt);
  add('Delivered',                item.meetingData?.deliveredAt);
  add('Failed',                   item.dlqData?.failedAt);

  events.sort((a, b) => new Date(a.ts) - new Date(b.ts));

  return (
    <Overlay onClick={onClose}>
      <ModalCard onClick={e => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Processing History</ModalTitle>
          <ModalCloseButton onClick={onClose}>✕</ModalCloseButton>
        </ModalHeader>
        <ModalBody>
          {events.length === 0 ? (
            <p style={{ color: '#64748b', margin: 0 }}>No timestamps available for this meeting.</p>
          ) : (
            events.map((ev, i) => (
              <div key={i} style={{ display: 'flex', gap: 16, marginBottom: 12, alignItems: 'flex-start' }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#0784a7', flexShrink: 0, marginTop: 5 }} />
                <div>
                  <div style={{ fontSize: 14, fontWeight: 500, color: '#1a2b3d' }}>{ev.label}</div>
                  <div style={{ fontSize: 12, color: '#64748b' }}>
                    {new Date(ev.ts).toLocaleString(undefined, {
                      month: 'short', day: 'numeric', year: 'numeric',
                      hour: '2-digit', minute: '2-digit', second: '2-digit',
                    })}
                  </div>
                </div>
              </div>
            ))
          )}
        </ModalBody>
      </ModalCard>
    </Overlay>
  );
}

// ─── Table ─────────────────────────────────────────────────────────────────────

function MeetingsTable({ items, loading, liveStore, adminStore, currentUserName }) {
  const [actionError, setActionError] = useState(null);

  const onAction = useCallback(async (fn) => {
    setActionError(null);
    try {
      await fn();
    } catch (err) {
      setActionError(err?.response?.data?.message || err.message || 'Action failed.');
    }
  }, []);

  return (
    <TableCard>
      <TableHeader>
        <TableHeaderCell />
        <TableHeaderCell>ID</TableHeaderCell>
        <TableHeaderCell>Meeting</TableHeaderCell>
        <TableHeaderCell>Stage</TableHeaderCell>
        <TableHeaderCell>Status</TableHeaderCell>
        <TableHeaderCell>Age</TableHeaderCell>
        <TableHeaderCell>Issue</TableHeaderCell>
        <TableHeaderCell />
      </TableHeader>

      {loading && items.length === 0 && (
        <>
          {[1, 2, 3, 4, 5].map(i => <SkeletonRow key={i} />)}
        </>
      )}

      {!loading && items.length === 0 && (
        <EmptyState>
          <EmptyStateIcon>📭</EmptyStateIcon>
          <EmptyStateText>No meetings match the current filter.</EmptyStateText>
        </EmptyState>
      )}

      {items.map(item => (
        <MeetingRow
          key={item.id}
          item={item}
          liveStore={liveStore}
          adminStore={adminStore}
          currentUserName={currentUserName}
          onAction={onAction}
        />
      ))}
    </TableCard>
  );
}

export default observer(MeetingsTable);
